<template>
  <div :id="`${source}_outcome_graph`">
    <ContainerInfoItem
      :source="`${source}_outcome_graph`"
      title="Booking Outcomes"
      :description="range?.label"
      height="full"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <PieChart
        v-else-if="chartLabels && chartData"
        :id="`${source}_outcome_chart`"
        :labels="chartLabels"
        :series-data="chartData"
        :primary-colors="['#06BC7E', '#A146E8', '#2579EF', '#FF4560']"
      />

      <EmptyScreen
        v-else
        :source="`${source}_outcome_graph`"
        text="No outcome data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import PieChart from "@/src/components/charts/PieChart.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import store from "@/src/store/index.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AnalyticService from "@/src/api/AnalyticService.js";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const chartLabels = ref(null);
const chartData = ref(null);
const isLoading = ref(false);

const processOutcomeData = (data) => {
  if (!data) {
    return { labels: null, seriesData: null };
  }

  const outcomeTypes = [
    {
      key: "meeting_booked_percentage",
      label: "Meeting Agent",
      color: "#06BC7E",
    },
    {
      key: "custom_message_percentage",
      label: "Custom Message",
      color: "#A146E8",
    },
    { key: "external_url_percentage", label: "External URL", color: "#2579EF" },
    { key: "drop_off_percentage", label: "Drop off", color: "#FF4560" },
  ];

  const filteredOutcomes = outcomeTypes.filter((type) => data[type.key] > 0);

  if (filteredOutcomes.length === 0) {
    return { labels: null, seriesData: null };
  }

  const labels = filteredOutcomes.map((type) => type.label);
  const seriesData = filteredOutcomes.map((type) => ({
    value: data[type.key],
    color: type.color,
  }));

  return { labels, seriesData };
};

const toFetchRoutingFormOutcome = async () => {
  try {
    isLoading.value = true;

    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      routing_form_id: filters.value?.routing_form?.id,
    };

    const response = await AnalyticService.V2.fetchRoutingFormOutcome(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;
      const { labels, seriesData } = processOutcomeData(data);

      chartLabels.value = labels;
      chartData.value = seriesData;
    } else {
      chartLabels.value = null;
      chartData.value = null;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching routing form outcome: ",
      error,
    );
    chartLabels.value = null;
    chartData.value = null;
  } finally {
    isLoading.value = false;
  }
};

watchEffect(() => {
  if (range.value) toFetchRoutingFormOutcome();
});

useAnalyticsRefresh(toFetchRoutingFormOutcome, [range]);

onMounted(() => {
  toFetchRoutingFormOutcome();
});
</script>
